import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { ArrowLeftBlack, ArrowRightBlack, ArrowUpBlack } from 'assets/icons';

interface CalendarProps {
  disabled?: boolean;
  busyDates?: string[];
  disableBusyDates?: boolean;
  disabledDays?: string[];
  disabledWeekDays?: number[];
  disableMonthControls?: boolean;
  maxDate?: string;
  minDate?: string;
  onChangeDate?: (date: string) => void;
  onSelectDate?: (date: string) => void;
  onSubmit?: (date: string) => void;
  value?: string;
}

interface CalendarState {
  daysInMonth: number;
  firstWeekDay: number;
  monthDays: number[];
  emptyDays: number[];
  currentMonth: string | null;
  prevMonth: string | null;
  nextMonth: string | null;
}

const Calendar: React.FC<CalendarProps> = ({
  disabled = false,
  busyDates,
  disableBusyDates = true,
  disabledDays,
  disabledWeekDays,
  disableMonthControls,
  maxDate,
  minDate,
  onChangeDate,
  onSelectDate,
  onSubmit,
  value,
}) => {
  const [showYearList, setShowYearList] = useState<boolean>(false);
  const [years, setYears] = useState<number[]>([]);
  const [date, setDate] = useState<string | undefined>();
  const [calendar, setCalendar] = useState<CalendarState>({
    daysInMonth: 0,
    firstWeekDay: 0,
    monthDays: [],
    emptyDays: [],
    currentMonth: null,
    prevMonth: null,
    nextMonth: null,
  });

  const setMonth = (month?: string | null) => {
    const daysInMonth = +moment(month ?? undefined)
      .endOf('month')
      .format('D');
    const firstWeekDay = +moment(month ?? undefined)
      .startOf('month')
      .format('d');

    const monthDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const emptyDays = Array.from({ length: firstWeekDay }, (_, i) => i + 1);

    const currentMonth = moment(month ?? undefined).format('YYYY-MM-01');
    const prevMonth = moment(month ?? undefined)
      .subtract(1, 'month')
      .format('YYYY-MM-01');
    const nextMonth = moment(month ?? undefined)
      .add(1, 'month')
      .format('YYYY-MM-01');

    setCalendar({
      daysInMonth,
      firstWeekDay,
      monthDays,
      emptyDays,
      currentMonth,
      prevMonth,
      nextMonth,
    });

    if (onChangeDate) onChangeDate(currentMonth);
  };

  const submit = () => {
    if (!date) {
      alert('Debe seleccionar la fecha');
      return;
    }
    if (onSubmit) {
      onSubmit(date);
    }
  };

  useEffect(() => {
    if (value && moment(value).isValid()) {
      setDate(value);
      setMonth(value);
    } else {
      setDate(undefined);
      setMonth(minDate);
    }

    const minYear = minDate ? moment(minDate).year() : moment().year() - 100;
    const maxYear = maxDate ? moment(maxDate).year() : moment().year() + 100;
    setYears(
      Array.from({ length: maxYear - minYear + 1 }, (_, i) => i + minYear),
    );
  }, [minDate, value, maxDate]);

  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  return (
    <div id="calendar-component">
      <div className="calendar__header">
        {!disableMonthControls && !showYearList && (
          <button
            className="calendar__month-action"
            onClick={() => setMonth(calendar.prevMonth)}
          >
            <img src={ArrowLeftBlack} alt="Previous Month" />
          </button>
        )}

        <div className="calendar__current">
          <div className="calendar__current-month">
            {calendar.currentMonth
              ? moment(calendar.currentMonth).locale('es').format('MMMM')
              : ''}
          </div>
          <button
            className="calendar__year-selector"
            onClick={() => setShowYearList(!showYearList)}
          >
            <span>
              {calendar.currentMonth
                ? moment(calendar.currentMonth).format('YYYY')
                : ''}
            </span>
            <img
              src={ArrowUpBlack}
              alt="Select Year"
              className={!showYearList ? 'rotate' : ''}
            />
          </button>
        </div>

        {!disableMonthControls && !showYearList && (
          <button
            className="calendar__month-action"
            onClick={() => setMonth(calendar.nextMonth)}
          >
            <img src={ArrowRightBlack} alt="Next Month" />
          </button>
        )}
      </div>

      {!showYearList && (
        <>
          <div className="calendar__weekdays">
            {weekDays.map((day, idx) => (
              <div key={idx} className="calendar__weekday">
                {day}
              </div>
            ))}
          </div>

          <div className="calendar__dates">
            {calendar.emptyDays.map((_, idx) => (
              <div key={`empty-${idx}`} className="calendar__day empty" />
            ))}

            {calendar.monthDays.map((day, idx) => {
              const currentDate = calendar.currentMonth
                ? moment(calendar.currentMonth)
                    .set('date', day)
                    .format('YYYY-MM-DD')
                : '';
              const isLessThanMin =
                minDate && moment(currentDate).isBefore(minDate);
              const isGreaterThanMax =
                maxDate && moment(currentDate).isAfter(maxDate);
              const isSelected = currentDate === date;
              const isWeekend =
                moment(currentDate).day() === 0 ||
                moment(currentDate).day() === 6;
              const isBusyDate = busyDates?.includes(currentDate);
              const isDisabledWeekDay = disabledWeekDays?.includes(
                moment(currentDate).isoWeekday() - 1,
              );
              const isDisabled =
                isDisabledWeekDay ||
                isLessThanMin ||
                isGreaterThanMax ||
                disabled;

              let className = 'calendar__day';
              let dayNumberClass = 'calendar__day-number';
              if (isSelected) dayNumberClass += ' selected';
              if (isBusyDate) className += ' busy';
              if (isDisabled) className += ' disabled';

              return (
                <div
                  key={`day-${idx}`}
                  className={className}
                  onClick={() => {
                    if (!isDisabled && !(isBusyDate && disableBusyDates)) {
                      setDate(currentDate);
                      if (onSelectDate) onSelectDate(currentDate);
                    }
                  }}
                >
                  <span className={dayNumberClass}>{day}</span>
                </div>
              );
            })}
          </div>

          {onSubmit && (
            <div className="calendar__actions">
              <button
                className="calendar__submit"
                disabled={!date}
                onClick={submit}
              >
                Aceptar
              </button>
            </div>
          )}
        </>
      )}

      {showYearList && (
        <div className="calendar__year-list">
          {years.map((year, idx) => (
            <div
              key={idx}
              className="calendar__year-item"
              onClick={() => {
                if (calendar.currentMonth) {
                  setMonth(
                    moment(calendar.currentMonth)
                      .year(year)
                      .format('YYYY-MM-01'),
                  );
                  setShowYearList(false);
                }
              }}
            >
              {year}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Calendar;
